import { inSpeedRun, madeDuringRun } from '@/lib/speedRun';
import { BrokerName } from '@/types/broker';
import { identifyPostHog } from '@/lib/posthog';
import { getViewAsUserId } from '@/lib/viewAs';
import { keyErrorClass, track } from '@/lib/analytics';
import { nextKeyRefusalNudge } from '@/lib/keyRefusal.core';
import { raiseLiveNudge } from '@/components/orbLive';
import i18n from '@/i18n';

export interface BrokerCredential {
  id: string;
  /****************
   * NO BACKEND SENDS broker_name. IT NEVER DID.
   *
   * It was declared required here and rendered in five places, two of them with
   * no fallback, so the card subtitle read "undefined • Stored securely in
   * backend" and the connect badge read "undefined • Live". A required field
   * that no producer populates is not a contract, it is a typo the compiler
   * agrees with — TypeScript checked every use against a promise nothing keeps.
   *
   * The real column is exchange_name ("phemex"), which the credential handlers
   * do send (handlers.go:59,68). Kept as optional only because callers still
   * reference it; every render now prefers exchange_name.
   ****************/
  broker_name?: string;
  exchange_name: string;
  is_active: boolean;
  is_testnet: boolean;
  label: string;
  created_at: string;
  // Hyperliquid's API wallets end on a date the owner chose: when, and how many
  // whole days are left. Absent for a broker whose keys do not expire.
  key_expires_at?: string;
  key_days_left?: number;
  /* Set on a key the user removed. The key stays on record with its name and
     its sessions; only its secrets are gone. Served only to includeRemoved. */
  removed_at?: string;
}

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const ACTIVE_CREDENTIAL_KEY_PREFIX = 'replicator-active-credential';

function getUserId(): string {
  /****************
   * IMPERSONATION APPLIES HERE TOO.
   *
   * Guillaume, 2026-09-07, looking at Gianni's screens: the Destination column
   * read "API #44" instead of the name he gave the key.
   *
   * This service talks to the API with a raw fetch rather than through
   * api/client.ts, and so never saw the ?viewAs= override that every other
   * call respects. Viewing somebody else's screens, it asked for the ADMIN's
   * credentials, found no id 44 among them, and fell back to printing the
   * internal id.
   *
   * The label was the visible symptom. The real one is that this returned the
   * wrong ACCOUNT's keys while claiming to show what the user sees -- the same
   * defect class as the admin badge that stayed on during impersonation.
   ****************/
  const viewAs = getViewAsUserId();
  if (viewAs) return viewAs;
  const clerkUser = (window as any).__clerk_user_id;
  return clerkUser || '';
}

function getActiveCredentialStorageKey(): string {
  const userId = getUserId();
  if (!userId) return `${ACTIVE_CREDENTIAL_KEY_PREFIX}:anonymous`;
  return `${ACTIVE_CREDENTIAL_KEY_PREFIX}:${userId}`;
}

export function setClerkUserId(userId: string) {
  (window as any).__clerk_user_id = userId;
  identifyPostHog(userId || null); // account id only (lib/posthog.ts)
}

function authHeaders(): Record<string, string> {
  const userId = getUserId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (userId) {
    headers['X-User-ID'] = userId;
  }
  return headers;
}

/* What CopyFi offers on a key and what the account has approved (backend builder_fee.go). */
export interface BuilderFeeStatus {
  available: boolean;
  rate?: string;
  fee_tenths?: number;
  approved_tenths: number;
  approved: boolean;
  account?: string;
}

export interface MetaTraderPairingInfo {
  id: number;
  label: string;
  token: string;
  server_url: string;
  ea_url: string;
  ea_source_url: string;
}

export interface MetaTraderStatus {
  exchange_name: string;
  connected: boolean;
  online: boolean;
  login?: number;
  server?: string;
  company?: string;
  kind?: string;
  margin_mode?: string;
  currency?: string;
  balance?: number;
  equity?: number;
  leverage?: number;
  algo_trading_on?: boolean;
  last_seen_seconds?: number;
  ea_version?: string;
  dry_run?: boolean;
}
export interface BuilderFeePrepared {
  typed_data: Record<string, unknown>;
  nonce: number;
  account: string;
  rate: string;
  action: 'approve' | 'revoke';
}

export const brokerCredentialsService = {
  /* includeRemoved: also the keys the user removed, for naming the sessions
     that traded on them (2026-10-24: a removed key's sessions read "API #0").
     Never for a picker or the key list -- a removed key cannot trade. */
  async getAllCredentials(opts?: { includeRemoved?: boolean }): Promise<BrokerCredential[]> {
    try {
      // Don't fetch if no user ID yet (auth not loaded)
      if (!getUserId()) return [];

      const query = opts?.includeRemoved ? '?include_removed=1' : '';
      const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials${query}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error('Failed to fetch credentials');
      const raw = await resp.json();
      // A speed run sees only a key added during the run (lib/speedRunLens.ts).
      return (raw || []).filter((cred: any) => !inSpeedRun() || madeDuringRun(cred.created_at)).map((cred: any) => ({
        ...cred,
        id: String(cred.id),
      }));
    } catch (error) {
      console.error('Error fetching credentials:', error);
      return [];
    }
  },

  async saveCredential(params: {
    name: BrokerName;
    label: string;
    passKey: string;
    secretKey: string;
    passphrase: string;
    isTestnet: boolean;
  }, attempt?: { attempt_number: number; ms_since_opened: number }): Promise<{ success: boolean; error?: string }> {
    try {
      if (!getUserId()) {
        return { success: false, error: i18n.t('copy:broker.toast.signedOut') };
      }
      const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          exchange_name: params.name.toLowerCase(),
          api_key: params.passKey,
          api_secret: params.secretKey,
          // KuCoin's third part, stored encrypted like the other two.
          ...(params.passphrase ? { api_passphrase: params.passphrase } : {}),
          is_testnet: params.isTestnet,
          label: params.label,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json();
        /*
         * The key was refused. The screen shows the reason; the Orb rings with
         * the walk-through, because a key is the step people most often give up
         * on (49 accounts had none on 2026-09-23). Not opened on top of the
         * error -- it waits by the Orb. orbLive.ts says why this exists.
         */
        // The second refusal of the same kind gets help for that kind; an
        // exchange that could not be reached is never blamed on the key (10-25).
        raiseLiveNudge({ ...nextKeyRefusalNudge(keyErrorClass(data.error)), force: true });
        return { success: false, error: data.error || i18n.t('copy:broker.toast.saveFailed') };
      }

      // The exchange and whether it is live -- never the key, the secret or
      // the passphrase. If a field would be awkward to show the user who
      // generated it, it does not belong in an event.
      track('broker_key_connected', `${params.name.toLowerCase()}:${params.isTestnet ? 'testnet' : 'live'}`, undefined, attempt);
      return { success: true };
    } catch (error) {
      console.error('Error in saveCredential:', error);
      return { success: false, error: i18n.t('copy:broker.toast.saveFailed') };
    }
  },

  async setActiveCredential(id: string): Promise<void> {
    localStorage.setItem(getActiveCredentialStorageKey(), String(id));
  },

  /****************
   * WHICH KEY IS ACTUALLY ACTIVE — AND IT IS NOT credential.is_active.
   *
   * "Active" here is a CLIENT choice: setActiveCredential writes the id to
   * localStorage and nothing else, because no server route exists to change it.
   * The credentials API is POST/GET/DELETE/verify only (routes.go:16-19).
   *
   * Meanwhile the server's is_active column is written true at creation
   * (handlers.go) and never updated by anything, so it reads true for every key
   * forever. The dialog was rendering its badge from that column, which means a
   * user with two keys saw BOTH marked "Active" while only one was being
   * traded — and the "Set Active" button, shown only when is_active is false,
   * could never appear at all.
   *
   * Reading the same storage the setter writes is the only answer that matches
   * what the app actually does.
   ****************/
  getActiveCredentialId(): string | null {
    try {
      return localStorage.getItem(getActiveCredentialStorageKey());
    } catch {
      return null; // private mode / storage disabled — nothing is pinned
    }
  },

  async deleteCredential(id: string): Promise<boolean> {
    try {
      if (!getUserId()) {
        console.error('Cannot delete credential: not authenticated');
        return false;
      }
      const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      return resp.ok;
    } catch (error) {
      console.error('Error deleting credential:', error);
      return false;
    }
  },

  /**
   * deleteCredential with the server's reason when it refuses. A live key whose
   * sessions still hold open positions is refused (409) with a sentence saying
   * what to do first; a bare `false` would show "delete failed" and nothing else.
   */
  async removeCredential(id: string): Promise<{ ok: boolean; error?: string; inUseBy?: number[] }> {
    try {
      if (!getUserId()) return { ok: false };
      const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (resp.ok) return { ok: true };
      const body = await resp.json().catch(() => ({}));
      // A key in use (11-03): the sessions to stop first, for the pop-up.
      const inUseBy = body?.code === 'key_in_use' && Array.isArray(body.session_ids) ? (body.session_ids as unknown[]).map(Number) : undefined;
      return { ok: false, error: typeof body?.error === 'string' ? body.error : undefined, inUseBy };
    } catch (error) {
      console.error('Error deleting credential:', error);
      return { ok: false };
    }
  },

  async getActiveCredential(): Promise<BrokerCredential | null> {
    const credentials = await this.getAllCredentials();
    const preferredId = localStorage.getItem(getActiveCredentialStorageKey());
    if (preferredId) {
      const preferred = credentials.find(cred => String(cred.id) === preferredId);
      if (preferred) return preferred;
    }
    return credentials.find(cred => cred.is_active) || credentials[0] || null;
  },

  /*
   * The endpoint answers with more than a yes/no: on success it returns the
   * venue label and the account's available and total balance
   * (handlers.go:313-318). Typing the result as {verified, error} threw all
   * three away, so a user who verified a key was told "it works" and shown
   * nothing that proves it — the balance IS the proof, and it also tells them
   * at a glance whether they pointed at testnet by mistake.
   */
  /* The fee CopyFi adds on a broker that has one, and what the account approved. */
  async getBuilderFee(id: string): Promise<BuilderFeeStatus | null> {
    try {
      const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials/${id}/builder-fee`, { headers: authHeaders() });
      return resp.ok ? await resp.json() : null;
    } catch {
      return null;
    }
  },

  /* The typed data to sign, with a fresh nonce. */
  async prepareBuilderFee(id: string, action: 'approve' | 'revoke'): Promise<BuilderFeePrepared> {
    const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials/${id}/builder-fee/prepare`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ action }),
    });
    const body = await resp.json();
    if (!resp.ok) throw new Error(body?.error || 'prepare failed');
    return body;
  },

  /* The signature the wallet produced; the server checks it and sends it on. */
  async submitBuilderFee(id: string, body: { action: 'approve' | 'revoke'; nonce: number; r: string; s: string; v: number }): Promise<{ sent: boolean; status?: BuilderFeeStatus }> {
    const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials/${id}/builder-fee`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    const out = await resp.json();
    if (!resp.ok) throw new Error(out?.error || 'submit failed');
    return out;
  },

  /* MetaTrader is paired, not typed in (backend api/metatrader.go). */
  async pairMetaTrader(label: string): Promise<MetaTraderPairingInfo> {
    const resp = await fetch(`${API_BASE}/api/v1/replicator/metatrader/pair`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ label }),
    });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(body?.error || 'pairing failed');
    return body;
  },

  async metaTraderStatus(id: string): Promise<MetaTraderStatus | null> {
    try {
      const resp = await fetch(`${API_BASE}/api/v1/replicator/metatrader/${id}/status`, { headers: authHeaders() });
      return resp.ok ? await resp.json() : null;
    } catch {
      return null;
    }
  },

  async verifyCredential(id: string): Promise<{
    verified: boolean;
    error?: string;
    venue?: string;
    available_balance?: number;
    total_balance?: number;
  }> {
    try {
      const resp = await fetch(`${API_BASE}/api/v1/replicator/credentials/${id}/verify`, {
        method: 'POST',
        headers: authHeaders(),
      });
      return await resp.json();
    } catch {
      /****************
       * NO ANSWER IS NOT A REFUSAL. 2026-10-08 (M-119).
       *
       * This returned { verified: false, error: 'Network error' }, so the
       * dialog showed "The exchange rejected this key" over an English
       * "Network error": a connection problem told as a bad key. Throwing
       * sends it down the dialog's own catch, which says the exchange could
       * not be reached and that this says nothing about the key, in the
       * reader's language.
       ****************/
      throw new Error(i18n.t('copy:broker.toast.unreachableDesc'));
    }
  },

  decryptCredentials(_credential: BrokerCredential): {
    passKey: string;
    secretKey: string;
    passphrase: string;
  } {
    throw new Error('Client-side decryption is disabled for security.');
  },
};
